import { useEffect, useLayoutEffect, useRef } from "react";
import { PreviewStateStore } from "@lib/preview";
import { useChatIsStreaming } from "@launch10/use-langgraph-stream";
import { useProjectUuid, useProjectId } from "@stores/projectStore";
import { useServerPreview } from "@hooks/useServerPreview";
import { useWebsiteChatState } from "./useWebsiteChat";
import { useWebsiteKickoffStore } from "@stores/websiteKickoffStore";

/**
 * Server-side preview driver. Builds the site on a real server and serves the
 * static result, then points the preview iframe at it.
 *
 * It drives the `PreviewStateStore`, so everything downstream follows: the
 * iframe, the page picker (which navigates to `displayUrl = previewUrl +
 * route`), the loading/error overlays, and the remount-on-change. The URL
 * source is `preview.<host>` — the static build uploaded to R2, served by Atlas.
 *
 * Lifecycle:
 *   - agent finishes a turn (`isStreaming` true→false), or first mount while
 *     idle → `mountStarted()` (loading overlay) + trigger a preview deploy.
 *   - build completes → `containerStateChanged(ready, preview.<host>)` +
 *     `iframeReload()` (remount). We do NOT mark rendered here — the build
 *     finishing only means the bytes are on R2, not on screen. The iframe's
 *     native `load` event (WebsitePreview `onLoad`) drives `iframeRendered()`,
 *     so the loading overlay stays up until the framed document has actually
 *     fetched and painted (no white-flash gap). The probe timer is the safety
 *     net if `load` never fires.
 *   - build fails → `containerStateChanged(error)`.
 *
 * Note: console-error capture and in-page-nav → picker sync require injecting a
 * preview-client into the build (follow-up). The page picker itself
 * (parent→iframe nav) works without it.
 */
/**
 * Whether to kick a preview rebuild. Pure so the gate is unit-testable without
 * the stream.
 *
 * The first-mount kick only fires for an EXISTING run — a site already built
 * before this session (`wasExistingRun`). A fresh site's first build must NOT
 * fire on mount: `isStreaming === false` at mount means the kickoff run hasn't
 * started streaming yet, not that it's done. Firing then would build before
 * WebsiteFiles::ForkFromTemplate copies package.json into website_files,
 * snapshotting a package.json-less site — the pnpm ERR_PNPM_NO_PKG_MANIFEST
 * crash. A fresh site's first preview instead arrives on the agent-done edge,
 * once the kickoff run has forked + built.
 *
 * After that, builds fire only on the agent-done edge when the agent actually
 * changed files. `agentChangedFiles` is default-safe: anything but an explicit
 * `false` (e.g. `undefined` before the first turn settles) still triggers a
 * build, so we never strand a stale preview.
 */
export function shouldKickPreview(args: {
  isStreaming: boolean;
  hasProject: boolean;
  doneEdge: boolean;
  kickedOnce: boolean;
  agentChangedFiles: boolean | undefined;
  wasExistingRun: boolean;
}): boolean {
  const { isStreaming, hasProject, doneEdge, kickedOnce, agentChangedFiles, wasExistingRun } = args;
  if (isStreaming || !hasProject) return false;
  const mountKick = !kickedOnce && wasExistingRun;
  return mountKick || (doneEdge && agentChangedFiles !== false);
}

export function useServerPreviewDriver(): void {
  const isStreaming = useChatIsStreaming();
  const projectUuid = useProjectUuid();
  const projectId = useProjectId();
  const { trigger, status, previewUrl, buildError, previewId } = useServerPreview(projectId);
  const agentChangedFiles = useWebsiteChatState("agentChangedFiles");
  // Shared arrival snapshot from useWebsiteAutoInit: was this an existing run
  // (already-built site) vs. a fresh site whose kickoff build is still pending?
  // null until autoInit publishes; treat null as "not yet" so the mount-kick
  // never fires before we know. See shouldKickPreview + websiteKickoffStore.
  const wasExistingRun = useWebsiteKickoffStore((s) => s.wasExistingRun);

  // Project switch: hand route ownership to the new project (same as WC driver).
  useLayoutEffect(() => {
    if (!projectUuid) return;
    if (PreviewStateStore.getState().projectUuid === projectUuid) return;
    PreviewStateStore.projectSwitched(projectUuid);
  }, [projectUuid]);

  // Drive the agent-working overlay + kick a rebuild on the agent-done edge
  // (and once on initial mount when the agent isn't running).
  const prevStreaming = useRef(isStreaming);
  const kickedOnce = useRef(false);
  useEffect(() => {
    const was = prevStreaming.current;
    prevStreaming.current = isStreaming;
    const doneEdge = was && !isStreaming;

    if (was !== isStreaming) {
      if (isStreaming) {
        PreviewStateStore.agentStreamStarted();
        return;
      }
      PreviewStateStore.agentStreamEnded();
    }

    // A text-only reply leaves agentChangedFiles false → skip the deploy + the
    // iframe reload (no flicker). See shouldKickPreview.
    if (
      shouldKickPreview({
        isStreaming,
        hasProject: !!projectId,
        doneEdge,
        kickedOnce: kickedOnce.current,
        agentChangedFiles,
        wasExistingRun: wasExistingRun === true,
      })
    ) {
      kickedOnce.current = true;
      PreviewStateStore.mountStarted();
      // Force a rebuild on the agent-done edge (the site just changed) so the
      // server's reuse check can't race the just-committed write and hand back a
      // stale build. A plain mount/reload (doneEdge false) allows reuse.
      void trigger(doneEdge);
    }
    // wasExistingRun is a dep: it publishes from null after autoInit runs (child
    // effects fire before the parent's), so the mount-kick for an existing site
    // must re-evaluate when it lands.
  }, [isStreaming, projectId, trigger, agentChangedFiles, wasExistingRun]);

  // Reflect each completed build into the existing preview UI.
  const shownPreview = useRef<number | null>(null);
  useEffect(() => {
    if (
      import.meta.env.DEV &&
      status === "completed" &&
      previewId &&
      shownPreview.current === previewId
    ) {
      // eslint-disable-next-line no-console
      console.error(
        `[GIVEUP-DEBUG] PROOF OF STICKINESS: preview ${previewId} just polled "completed" ` +
          `but was already consumed as failed (shownPreview latch) — the completed branch is ` +
          `skipped, so the error card stays up despite a successful build.`,
      );
    }
    if (
      status === "completed" &&
      previewUrl &&
      previewId &&
      shownPreview.current !== previewId
    ) {
      shownPreview.current = previewId;
      PreviewStateStore.containerStateChanged({
        status: "ready",
        previewUrl,
        error: null,
      });
      // Remount the iframe at the new build, but leave render.phase "pending":
      // the loading overlay stays up until the iframe's own `load` event fires
      // (WebsitePreview onLoad → iframeRendered). Marking rendered here — off the
      // build-completed poll — tore the overlay down 1-3s before the iframe had
      // fetched and painted, which is the white-flash the user sees.
      PreviewStateStore.iframeReload();
    } else if (status === "failed" && previewId && shownPreview.current !== previewId) {
      shownPreview.current = previewId;
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.error(
          `[GIVEUP-DEBUG] buildFailed() called for preview ${previewId} ` +
            `(buildError=${JSON.stringify(buildError ?? null)}). Error card renders now; ` +
            `this previewId is latched, so its real "completed" status will be ignored.`,
        );
      }
      // A failed build is a CONTENT error (the agent wrote something that won't
      // compile) — surface the sanitized message via the BuildErrorCard + "Fix
      // errors" loop, NOT the generic container-error/page-reload path.
      PreviewStateStore.buildFailed(buildError ?? null);
    }
  }, [status, previewUrl, buildError, previewId]);
}
